/*
  Neroli — predictive-search.js

  Uses Shopify's native /search/suggest.json endpoint. No custom backend.

  One engine, two hosts:
    <predictive-search-inline>  — dropdown under the header search bar
    <predictive-search>         — full-screen overlay (mobile / fallback)

  Both expose [data-predictive-input] and [data-predictive-results];
  the engine doesn't care which host it lives in.
*/

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 6;

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str == null ? '' : String(str);
  return div.innerHTML;
}

function routeRoot() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

/* ==================================================================
   Engine
   ================================================================== */
class PredictiveSearchEngine {
  constructor(host) {
    this.host = host;
    this.input = host.querySelector('[data-predictive-input]');
    this.results = host.querySelector('[data-predictive-results]');
    this.category = host.querySelector('[data-search-category]');
    this.clearButton = host.querySelector('[data-search-clear]');
    this.form = host.querySelector('[data-search-form]');

    if (!this.input || !this.results) return;

    this.controller = null;
    this.cache = new Map();
    this.activeTab = 'products';
    this.lastData = null;
    this.lastQuery = '';

    this.handleInput = debounce(this.handleInput.bind(this), DEBOUNCE_MS);
    this.onRawInput = this.onRawInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onResultsClick = this.onResultsClick.bind(this);

    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('input', this.onRawInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('focus', this.onFocus);
    this.results.addEventListener('click', this.onResultsClick);

    this.clearButton?.addEventListener('click', () => this.clear());

    this.category?.addEventListener('change', () => {
      const q = this.input.value.trim();
      if (q.length >= MIN_QUERY_LENGTH) this.search(q);
    });

    this.form?.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });

    this.syncClearButton();
  }

  destroy() {
    this.controller?.abort();
    this.input?.removeEventListener('input', this.handleInput);
    this.input?.removeEventListener('input', this.onRawInput);
    this.input?.removeEventListener('keydown', this.onKeydown);
    this.input?.removeEventListener('focus', this.onFocus);
    this.results?.removeEventListener('click', this.onResultsClick);
  }

  /* ---- Input ---- */

  onRawInput() {
    // Undebounced so the clear button feels instant.
    this.syncClearButton();
  }

  syncClearButton() {
    if (!this.clearButton) return;
    this.clearButton.hidden = this.input.value.trim() === '';
  }

  handleInput(event) {
    const query = event.target.value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      this.hide();
      return;
    }
    this.search(query);
  }

  clear() {
    this.input.value = '';
    this.syncClearButton();
    this.hide();
    this.input.focus();
  }

  onFocus() {
    if (
      this.results.innerHTML.trim() !== '' &&
      this.input.value.trim().length >= MIN_QUERY_LENGTH
    ) {
      this.show();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.hide();
      this.input.blur();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const links = Array.from(this.results.querySelectorAll('a')).filter(
      (a) => a.offsetParent !== null
    );
    if (links.length === 0) return;

    event.preventDefault();
    const current = links.indexOf(document.activeElement);

    let next;
    if (event.key === 'ArrowDown') {
      next = current < 0 ? 0 : Math.min(current + 1, links.length - 1);
    } else {
      next = current <= 0 ? -1 : current - 1;
    }

    if (next < 0) this.input.focus();
    else links[next].focus();
  }

  onResultsClick(event) {
    const tab = event.target.closest('[data-search-tab]');
    if (!tab) return;
    event.preventDefault();
    this.activeTab = tab.dataset.searchTab;
    if (this.lastData) this.render(this.lastData, this.lastQuery);
  }

  /* ---- Fetch ---- */

  buildUrl(query) {
    let q = query;

    // The suggest endpoint has no type/collection facet, so a chosen
    // category biases the query rather than filtering it.
    if (this.category?.value) {
      q = `${query} ${this.category.value}`;
    }

    const params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'product,collection,article,page,query');
    params.set('resources[limit]', String(RESULT_LIMIT));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    return `${routeRoot()}search/suggest.json?${params}`;
  }

  async search(query) {
    const url = this.buildUrl(query);

    if (this.cache.has(url)) {
      this.render(this.cache.get(url), query);
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    this.host.setAttribute('data-loading', 'true');

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const json = await response.json();
      const data = json?.resources?.results || {};

      this.cache.set(url, data);
      this.render(data, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Predictive search:', error);
      this.hide();
    } finally {
      this.host.removeAttribute('data-loading');
    }
  }

  /* ---- Render ---- */

  render(data, query) {
    this.lastData = data;
    this.lastQuery = query;

    const products = data.products || [];
    const collections = data.collections || [];
    const articles = data.articles || [];
    const pages = data.pages || [];
    const queries = data.queries || [];

    const hasResults =
      products.length || collections.length || articles.length || pages.length;

    if (!hasResults && queries.length === 0) {
      this.results.innerHTML = `
        <div class="search-results__empty">
          <p>${escapeHtml(`No results found for "${query}"`)}</p>
        </div>`;
      this.show();
      return;
    }

    // Tabs only earn their place when more than one resource type matched.
    const tabs = [];
    if (products.length) tabs.push({ id: 'products', label: 'Products' });
    if (collections.length) tabs.push({ id: 'collections', label: 'Collections' });
    if (articles.length) tabs.push({ id: 'articles', label: 'Articles' });
    if (pages.length) tabs.push({ id: 'pages', label: 'Pages' });

    if (!tabs.some((t) => t.id === this.activeTab)) {
      this.activeTab = tabs[0]?.id || 'products';
    }

    const searchUrl = `${routeRoot()}search?q=${encodeURIComponent(query)}&type=product`;

    this.results.innerHTML = `
      <div class="search-results__layout">
        <div class="search-results__main">
          ${tabs.length > 1 ? this.renderTabs(tabs) : ''}
          ${this.renderPanel(data)}
          <a href="${searchUrl}" class="search-results__view-all">
            View all search results
            <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
        ${queries.length ? this.renderSuggestions(queries, query) : ''}
      </div>`;

    this.show();
  }

  renderTabs(tabs) {
    return `
      <div class="search-results__tabs" role="tablist">
        ${tabs
          .map(
            (tab) => `
              <button
                type="button"
                role="tab"
                class="search-results__tab${tab.id === this.activeTab ? ' is-active' : ''}"
                data-search-tab="${tab.id}"
                aria-selected="${tab.id === this.activeTab}"
              >${tab.label}</button>`
          )
          .join('')}
      </div>`;
  }

  renderPanel(data) {
    switch (this.activeTab) {
      case 'products':
        return this.renderProducts(data.products || []);
      case 'collections':
        return this.renderList(data.collections || []);
      case 'articles':
        return this.renderList(data.articles || []);
      case 'pages':
        return this.renderList(data.pages || []);
      default:
        return '';
    }
  }

  /*
    suggest.json returns prices as bare decimal strings ("600.00"), with no
    currency. Format them against the shop's active currency, and split the
    cents so they can be rendered as a superscript.
  */
  formatPrice(value) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return null;

    const currency =
      window.Shopify?.currency?.active ||
      document.documentElement.dataset.currency ||
      'USD';

    let formatted;
    try {
      formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      // Unknown currency code — fall back to a plain decimal.
      formatted = amount.toFixed(2);
    }

    // Split trailing cents so the template can superscript them.
    const match = formatted.match(/^(.*?)(\d{2})$/);
    if (!match) return { whole: formatted, cents: '', currency };

    return {
      whole: match[1].replace(/[.,]$/, ''),
      cents: match[2],
      currency,
    };
  }

  /*
    A product spans a price range when its variants disagree. The suggest
    payload doesn't give price_min/price_max, so derive it from variants.
  */
  priceLabel(product) {
    const price = this.formatPrice(product.price);
    if (!price) return '';

    const variants = product.variants || [];
    const prices = variants
      .map((v) => Number.parseFloat(v.price))
      .filter((n) => Number.isFinite(n));

    const hasRange = prices.length > 1 && Math.min(...prices) !== Math.max(...prices);

    const cents = price.cents
      ? `<sup class="search-results__product-cents">${price.cents}</sup>`
      : '';

    return `
      ${hasRange ? '<span class="search-results__product-from">From</span> ' : ''}
      ${escapeHtml(price.whole)}${cents}
      <span class="search-results__product-currency">${escapeHtml(price.currency)}</span>`;
  }

  renderProducts(products) {
    if (products.length === 0) return '';

    return `
      <ul class="search-results__products" role="list">
        ${products
          .map((product) => {
            const image = product.featured_image?.url || product.image || '';
            const priceMarkup = this.priceLabel(product);

            return `
              <li class="search-results__product">
                <a href="${product.url}">
                  <span class="search-results__product-media">
                    ${
                      image
                        ? `<img src="${image}" alt="${escapeHtml(
                            product.title
                          )}" loading="eager" decoding="sync" fetchpriority="high" width="56" height="56">`
                        : ''
                    }
                  </span>
                  <span class="search-results__product-info">
                    <span class="search-results__product-title">${escapeHtml(
                      product.title
                    )}</span>
                    ${
                      priceMarkup
                        ? `<span class="search-results__product-price">${priceMarkup}</span>`
                        : ''
                    }
                  </span>
                </a>
              </li>`;
          })
          .join('')}
      </ul>`;
  }

  renderList(items) {
    if (items.length === 0) return '';
    return `
      <ul class="search-results__list" role="list">
        ${items
          .map((item) => `<li><a href="${item.url}">${escapeHtml(item.title)}</a></li>`)
          .join('')}
      </ul>`;
  }

  renderSuggestions(queries, query) {
    const lower = query.toLowerCase();

    return `
      <div class="search-results__suggestions">
        <p class="search-results__suggestions-heading">Suggestions</p>
        <ul role="list">
          ${queries
            .map((item) => {
              const text = item.text || '';

              const markup = escapeHtml(text);
              const url =
                item.url || `${routeRoot()}search?q=${encodeURIComponent(text)}`;
              return `<li><a href="${url}">${markup}</a></li>`;
            })
            .join('')}
        </ul>
      </div>`;
  }

  show() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  hide() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

/* ==================================================================
   Voice search — progressive enhancement.
   Removed entirely where the Web Speech API is unavailable (e.g. Firefox).
   ================================================================== */
class VoiceSearch {
  static get supported() {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  constructor(host) {
    this.button = host.querySelector('[data-search-voice]');
    // Voice search can attach to a plain search input too — not every
    // host runs predictive results (e.g. the search results page).
    this.input =
      host.querySelector('[data-predictive-input]') ||
      host.querySelector('.search-form__input');
    if (!this.button || !this.input) return;

    if (!VoiceSearch.supported) {
      this.button.remove();
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false;
    this.recognition.interimResults = false;
    this.recognition.lang = document.documentElement.lang || 'en-US';

    this.button.hidden = false;
    this.button.addEventListener('click', () => this.start());

    this.recognition.addEventListener('result', (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      if (!transcript) return;
      this.input.value = transcript;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    this.recognition.addEventListener('end', () => this.stop());
    this.recognition.addEventListener('error', () => this.stop());
  }

  start() {
    this.button.classList.add('is-listening');
    this.button.setAttribute('aria-pressed', 'true');
    try {
      this.recognition.start();
    } catch (e) {
      // start() throws if already running — harmless.
    }
  }

  stop() {
    this.button.classList.remove('is-listening');
    this.button.setAttribute('aria-pressed', 'false');
  }
}

/* ==================================================================
   Hosts
   ================================================================== */
class PredictiveSearchInline extends HTMLElement {
  connectedCallback() {
    this.engine = new PredictiveSearchEngine(this);
    this.voice = new VoiceSearch(this);

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.activate = this.activate.bind(this);
    this.deactivate = this.deactivate.bind(this);

    // One backdrop, shared. The overlay host may also want it, and two
    // stacked backdrops would double the dimming.
    this.backdrop = document.querySelector('.search-backdrop');
    if (!this.backdrop) {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'search-backdrop';
      document.body.appendChild(this.backdrop);
    }

    this.backdrop.addEventListener('click', this.deactivate);
    // Activate on typing, not focus — the reference only dims once a
    // query exists, and focus-activation races with the document click
    // handler, causing a visible in/out flicker.
    this.engine?.input?.addEventListener('input', () => {
      if (this.engine.input.value.trim().length >= 2) this.activate();
      else this.deactivate();
    });
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.engine?.destroy();
    this.backdrop?.removeEventListener('click', this.deactivate);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.deactivate();
  }

  activate() {
    this.classList.add('is-active');
    this.backdrop.classList.add('is-active');
  }

  deactivate() {
    this.classList.remove('is-active');
    this.backdrop.classList.remove('is-active');
    this.engine?.hide();
  }

  onDocumentClick(event) {
    // mousedown inside the card can register as a click on document when
    // selection drags outside — only dismiss on genuine outside clicks.
    if (this.contains(event.target) || event.target === this.backdrop) return;
    this.deactivate();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.deactivate();
  }
}

customElements.define('predictive-search-inline', PredictiveSearchInline);

class PredictiveSearchOverlay extends HTMLElement {
  connectedCallback() {
    this.engine = new PredictiveSearchEngine(this);
    this.voice = new VoiceSearch(this);

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onOpenClick = this.onOpenClick.bind(this);

    // Delegated: the theme editor re-renders the header, which would
    // otherwise leave listeners bound to detached trigger nodes.
    document.addEventListener('click', this.onOpenClick);

    this.querySelectorAll('[data-predictive-search-close]').forEach((el) => {
      el.addEventListener('click', this.close);
    });
  }

  disconnectedCallback() {
    this.engine?.destroy();
    document.removeEventListener('click', this.onOpenClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  }

  onOpenClick(event) {
    if (!event.target.closest('[data-predictive-search-open]')) return;
    event.preventDefault();
    this.open();
  }

  open() {
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
    requestAnimationFrame(() => this.engine?.input?.focus());
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }
}

customElements.define('predictive-search', PredictiveSearchOverlay);
